import { useState } from "react";
import { motion } from "framer-motion";
import { PROMOTERS, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

function PromoterVisual({ promoter }) {
    const [imageFailed, setImageFailed] = useState(false);
    const initials = promoter.name
        .split(" ")
        .map((n) => n[0])
        .join("");
    const showImage = promoter.image && !imageFailed;

    return (
        <div className="aspect-[3/4] relative overflow-hidden">
            {showImage ? (
                <>
                    <img
                        src={promoter.image}
                        alt={promoter.name}
                        className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        onError={() => setImageFailed(true)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
                </>
            ) : (
                <>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35]/30 via-[#9D4CDD]/20 to-[#0a0a0a]" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-display text-[8rem] font-black text-white/10 group-hover:text-white/20 transition-colors duration-700">
                            {initials}
                        </span>
                    </div>
                </>
            )}

            <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF6B35] mb-2">
                    0{promoter.index}
                </div>
                <h3 className="font-display text-2xl font-bold leading-tight">{promoter.name}</h3>
                {promoter.role && (
                    <div className="text-xs uppercase tracking-widest text-zinc-400 mt-1">{promoter.role}</div>
                )}
            </div>
        </div>
    );
}

export default function Promoters() {
    return (
        <section
            id="promoters"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="promoters-section"
        >
            <SectionBg src={PDF_BG.promoters} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        The Promoters
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        The four people <span className="gradient-text">obsessed</span> with putting Jaipur on the map.
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {PROMOTERS.map((p, i) => (
                        <motion.div
                            key={p.name}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ delay: i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -6 }}
                            className="group relative rounded-3xl border border-white/10 bg-[#141414] overflow-hidden hover:border-[#FF6B35]/50 transition-all duration-500"
                            data-testid={`promoter-card-${i}`}
                        >
                            <PromoterVisual promoter={{ ...p, index: i + 1 }} />
                            <div className="p-6 border-t border-white/10">
                                <p className="text-sm text-zinc-400 leading-relaxed">{p.bio}</p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
